// Shared helpers for the Call Scheduling feature.
// The API speaks UTC ISO on the wire; the business runs in America/New_York,
// so all admin-facing times are formatted in Eastern regardless of browser tz.
import React from 'react';
import { FaCalendarCheck, FaCheckCircle, FaBan, FaUserSlash } from 'react-icons/fa';

export const BUSINESS_TZ = 'America/New_York';

// "Jun 26, 2026, 2:00 PM" in Eastern.
export const formatEastern = (iso, opts = {}) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    timeZone: BUSINESS_TZ,
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
    ...opts,
  });
};

// "2:00 PM" Eastern (time only).
export const formatEasternTime = (iso) =>
  formatEastern(iso, { month: undefined, day: undefined, year: undefined });

// "Friday, June 26" Eastern (day only).
export const formatEasternDay = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    timeZone: BUSINESS_TZ, weekday: 'long', month: 'long', day: 'numeric',
  });
};

// Compare an ISO instant to "today"/"tomorrow" in Eastern (YYYY-MM-DD bucket).
const easternDateKey = (date) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date);

export const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return false;
  return easternDateKey(d) === easternDateKey(new Date());
};

export const isTomorrow = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return false;
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
  return easternDateKey(d) === easternDateKey(tomorrow);
};

// A relative label ("Today", "Tomorrow") or the Eastern day, for highlight UI.
export const relativeDayLabel = (iso) => {
  if (isToday(iso)) return 'Today';
  if (isTomorrow(iso)) return 'Tomorrow';
  return formatEasternDay(iso);
};

// Booking status presentation (colors chosen to match existing status badges).
export const STATUS_META = {
  scheduled: { label: 'Scheduled', color: '#4FC3F7', icon: <FaCalendarCheck size={10} /> },
  completed: { label: 'Completed', color: '#66BB6A', icon: <FaCheckCircle size={10} /> },
  canceled:  { label: 'Canceled',  color: '#999',    icon: <FaBan size={10} /> },
  no_show:   { label: 'No-show',   color: '#ef5350', icon: <FaUserSlash size={10} /> },
};

export const statusMeta = (status) => STATUS_META[status] || STATUS_META.scheduled;

export const STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'completed', label: 'Completed' },
  { value: 'no_show', label: 'No-show' },
  { value: 'canceled', label: 'Canceled' },
];

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
